import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  Alert,
  Avatar,
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import MarkEmailUnreadRoundedIcon from "@mui/icons-material/MarkEmailUnreadRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import IconTimeCapsuleMemory from "../../assets/icons/v2/time-capsule-memory.webp";
import IconTimeCapsuleOpened from "../../assets/icons/v2/time-capsule-opened.webp";
import useUserHistory from "../../hooks/useUserHistory";
import { QUICK_FIND_LABELS } from "../../services/historyService";
import useUserProfileStore, { selectUserProfile } from "../../store/useUserProfileStore";
import { getLovedTimeCapsuleIds } from "../../services/profileService";
import { fetchTimeCapsulesByIds, fetchLovedTimeCapsules } from "../../services/timeCapsuleService";
import { isVideoMediaType, isLikelyVideoUrl } from "../../utils/mediaUrl";
import { APP_SHELL_LAYOUT } from "../../constants/constants";

const BASE_FILTERS = [
  { key: "all", label: "All" },
  { key: "find", label: "Find" },
  { key: "clicked", label: "Opened" },
  { key: "personal", label: "Memories" },
  { key: "received", label: "Received" },
  { key: "loved", label: "Loved" },
  { key: "hidden", label: "Hidden", secondary: true },
];

function Stat({ label, value, icon }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        flex: 1,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mb: 0.8 }}>
        <Box sx={{ display: "flex", color: "rgba(230,234,242,0.76)", "& svg": { fontSize: 18 } }}>
          {icon}
        </Box>
        <Typography sx={{ color: "rgba(230,234,242,0.76)", fontSize: 12, fontWeight: 800, lineHeight: 1.1 }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ color: "common.white", fontSize: 28, fontWeight: 900, lineHeight: 1, letterSpacing: 0 }}>
        {value}
      </Typography>
    </Box>
  );
}

function fallbackIconFor(item) {
  if (item?.filterType === "find") return <SearchRoundedIcon sx={{ fontSize: 38 }} />;
  if (item?.filterType === "loved") return <FavoriteRoundedIcon sx={{ fontSize: 38 }} />;
  const isOpened = item?.badge === "Opened" || item?.filterType === "clicked";
  const isReceived = item?.filterType === "received";
  const isLargeFallback = isOpened || isReceived;
  return (
    <Box
      component="img"
      data-testid={
        isOpened
          ? "history-opened-fallback-icon"
          : isReceived
            ? "history-received-fallback-icon"
            : undefined
      }
      src={isOpened ? IconTimeCapsuleOpened : IconTimeCapsuleMemory}
      alt=""
      sx={{ width: isLargeFallback ? 58 : 42, height: isLargeFallback ? 58 : 42, objectFit: "contain" }}
    />
  );
}

function cleanImageUrl(value = "") {
  const url = String(value || "").trim();
  return url && !isLikelyVideoUrl(url) ? url : "";
}

function mediaObjectImageCandidates(value = {}) {
  const previewCandidates = [
    value.sessionPreviewUrl,
    value.posterUrl,
    value.thumbnailUrl,
    value.imageUrl,
    value.imageURL,
    value.photoUrl,
    value.photoURL,
  ];
  if (isVideoMediaType(value.type || value.contentType || value.mimeType)) {
    return previewCandidates;
  }
  return [
    ...previewCandidates,
    value.downloadURL,
    value.url,
  ];
}

function firstImageUrl(...values) {
  const flattened = values.flatMap((value) => {
    if (!value) return [];
    if (typeof value === "string") return [cleanImageUrl(value)].filter(Boolean);
    if (Array.isArray(value)) return value.flatMap((entry) => firstImageUrl(entry));
    if (typeof value === "object") {
      return mediaObjectImageCandidates(value).map(cleanImageUrl).filter(Boolean);
    }
    return [];
  });
  return String(flattened.find((url) => String(url || "").trim()) || "").trim();
}

function historyTileThumbnailUrl(item = {}) {
  return firstImageUrl(
    item.thumbnailUrl,
    item.imageUrl,
    item.imageURL,
    item.photoUrl,
    item.photoURL,
    item.previewImageUrl,
    item.heroImageUrl,
    item.coverImageUrl,
    item.resultImageUrl,
    item.placeImageUrl,
    item.placePhotoUrl,
    item.media,
    item.mediaFiles,
    item.previewMedia,
    item.photoUrls,
    item.photoURLs,
    item.images,
    item.photos
  );
}

function firstCapsuleMediaUrl(capsule = {}) {
  return firstImageUrl(
    capsule.thumbnailUrl,
    capsule.imageUrl,
    capsule.imageURL,
    capsule.photoUrl,
    capsule.photoURL,
    capsule.previewImageUrl,
    capsule.heroImageUrl,
    capsule.coverImageUrl,
    capsule.resultImageUrl,
    capsule.placeImageUrl,
    capsule.placePhotoUrl,
    capsule.media,
    capsule.mediaFiles,
    capsule.previewMedia,
    capsule.photoUrls,
    capsule.photoURLs,
    capsule.images,
    capsule.photos
  );
}

function capsuleTitle(capsule = {}) {
  return String(capsule.messageTitle || capsule.title || capsule.placeName || capsule.location?.placeName || "Time capsule").trim();
}

function capsuleSubtitle(capsule = {}) {
  return String(capsule.location?.address || capsule.location?.placeName || capsule.address || "").trim();
}

function buildLovedHistoryItem(capsule = {}) {
  const capsuleId = String(capsule?.id || capsule?.capsuleId || "").trim();
  if (!capsuleId) return null;
  return {
    id: `loved-${capsuleId}`,
    type: "loved",
    filterType: "loved",
    capsuleId,
    title: capsuleTitle(capsule),
    subtitle: capsuleSubtitle(capsule),
    thumbnailUrl: firstCapsuleMediaUrl(capsule),
    badge: "Loved",
    capsuleType: String(capsule?.capsuleType || "").trim(),
    href: `/capsule/${encodeURIComponent(capsuleId)}`,
  };
}

function sortLovedHistoryItems(items = [], sort = "recent") {
  const rows = Array.isArray(items) ? items : [];
  return sort === "oldest" ? rows : [...rows].reverse();
}

function findAreaLabel(findItem = {}) {
  const locationLabel = String(findItem?.locationLabel || "").trim();
  return locationLabel ? `Near ${locationLabel}` : "Near me";
}

function normalizeFindPrompt(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function findDisplayTitle(findItem = {}) {
  const quickFindCategoryKey = String(findItem?.quickFindCategoryKey || "").trim().toLowerCase();
  if (QUICK_FIND_LABELS[quickFindCategoryKey]) {
    return QUICK_FIND_LABELS[quickFindCategoryKey];
  }
  const prompt = String(findItem?.prompt || findItem?.title || "").trim();
  const normalized = normalizeFindPrompt(prompt);
  if (normalized.includes("find great nearby places to eat, drink, and have fun")) {
    return "Food, drinks, and fun nearby";
  }
  if (normalized.includes("find great nearby places to eat around this map area")) {
    return "Food nearby";
  }
  if (normalized.includes("find great nearby places to drink around this map area")) {
    return "Drinks nearby";
  }
  if (normalized.includes("find fun nearby things to do around this map area")) {
    return "Fun nearby";
  }
  return prompt || "Find";
}

function hasRenderableHistoryResult(item = {}) {
  if (item?.type !== "find") return true;
  return Array.isArray(item?.capsuleIds) && item.capsuleIds.some((id) => String(id || "").trim());
}

export default function HistoryPage() {
  const navigate = useNavigate();
  const uid = getAuth().currentUser?.uid || "";
  const userProfile = useUserProfileStore(selectUserProfile);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [activeFind, setActiveFind] = useState(null);
  const [findResults, setFindResults] = useState([]);
  const [findResultsLoading, setFindResultsLoading] = useState(false);
  const [findResultsError, setFindResultsError] = useState("");
  const [hiddenSnackbar, setHiddenSnackbar] = useState({ open: false, item: null });
  const [lovedCapsules, setLovedCapsules] = useState([]);
  const [lovedLoading, setLovedLoading] = useState(false);
  const findRequestIdRef = useRef(0);
  const { items, hiddenItems, summary, loading, error, hideHistoryItem, restoreHistoryItem } = useUserHistory(uid, {
    filter: filter === "hidden" ? "all" : filter,
    sort,
  });
  const isHiddenView = filter === "hidden";
  const lovedCapsuleIds = useMemo(() => getLovedTimeCapsuleIds(userProfile), [userProfile]);

  useEffect(() => {
    if (!uid || !lovedCapsuleIds.length) {
      setLovedCapsules([]);
      setLovedLoading(false);
      return undefined;
    }

    let active = true;
    setLovedLoading(true);
    fetchLovedTimeCapsules(lovedCapsuleIds, uid)
      .then((capsules) => {
        if (active) setLovedCapsules(Array.isArray(capsules) ? capsules : []);
      })
      .catch((err) => {
        console.warn("Failed to load loved History capsules:", err);
        if (active) setLovedCapsules([]);
      })
      .finally(() => {
        if (active) setLovedLoading(false);
      });

    return () => {
      active = false;
    };
  }, [lovedCapsuleIds, uid]);

  const lovedItems = useMemo(() => {
    const capsuleById = new Map(
      (Array.isArray(lovedCapsules) ? lovedCapsules : [])
        .filter((capsule) => capsule?.id || capsule?.capsuleId)
        .map((capsule) => [String(capsule.id || capsule.capsuleId).trim(), capsule])
    );
    const orderedItems = lovedCapsuleIds
      .map((capsuleId) => buildLovedHistoryItem(capsuleById.get(capsuleId)))
      .filter(Boolean);
    return sortLovedHistoryItems(orderedItems, sort);
  }, [lovedCapsuleIds, lovedCapsules, sort]);

  const filters = useMemo(
    () =>
      BASE_FILTERS.map((option) =>
        option.key === "loved"
          ? { ...option, label: `Loved (${lovedItems.length})` }
          : option
      ),
    [lovedItems.length]
  );

  const visibleItems = useMemo(
    () => {
      if (isHiddenView) return (Array.isArray(hiddenItems) ? hiddenItems : []).filter(hasRenderableHistoryResult);
      if (filter === "loved") return lovedItems.filter(hasRenderableHistoryResult);
      if (filter === "all") {
        return [
          ...lovedItems,
          ...(Array.isArray(items) ? items : []),
        ].filter(hasRenderableHistoryResult);
      }
      return (Array.isArray(items) ? items : []).filter(hasRenderableHistoryResult);
    },
    [filter, hiddenItems, isHiddenView, items, lovedItems]
  );
  const pageLoading = loading || (filter === "loved" && lovedLoading);

  const openFindResults = async (item) => {
    const capsuleIds = Array.isArray(item?.capsuleIds) ? item.capsuleIds.filter(Boolean) : [];
    setActiveFind(item);
    setFindResults([]);
    setFindResultsError("");
    if (!capsuleIds.length) return;
    // Ignore the response if a newer Find has been opened in the meantime.
    const requestId = findRequestIdRef.current + 1;
    findRequestIdRef.current = requestId;
    setFindResultsLoading(true);
    try {
      const capsules = await fetchTimeCapsulesByIds(capsuleIds, uid);
      if (requestId !== findRequestIdRef.current) return;
      setFindResults(Array.isArray(capsules) ? capsules : []);
    } catch (err) {
      console.warn("Failed to load Find history capsules:", err);
      if (requestId !== findRequestIdRef.current) return;
      setFindResultsError("Those Find results are temporarily unavailable.");
    } finally {
      if (requestId === findRequestIdRef.current) setFindResultsLoading(false);
    }
  };

  const handleOpenItem = (item) => {
    if (item?.type === "find") {
      openFindResults(item);
      return;
    }
    if (item?.href) navigate(item.href);
  };

  const handleHideItem = async (event, item) => {
    event.stopPropagation();
    if (!item?.id) return;
    try {
      await hideHistoryItem(item.id);
      setHiddenSnackbar({ open: true, item });
    } catch (err) {
      console.warn("Failed to hide history item:", err);
    }
  };

  const handleUndoHide = async () => {
    const item = hiddenSnackbar.item;
    if (!item?.id) return;
    try {
      await restoreHistoryItem(item.id);
      setHiddenSnackbar({ open: false, item: null });
    } catch (err) {
      console.warn("Failed to restore hidden history item:", err);
    }
  };

  const handleRestoreItem = async (event, item) => {
    event.stopPropagation();
    if (!item?.id) return;
    try {
      await restoreHistoryItem(item.id);
    } catch (err) {
      console.warn("Failed to restore hidden history item:", err);
    }
  };

  return (
    <Box
      sx={(theme) => ({
        minHeight: "100%",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.colors.mediumBlue, 0.18)} 100%)`
            : "linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%)",
        pb: `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 24px)`,
      })}
    >
      <Container
        maxWidth="sm"
        sx={{
          px: 2,
          pt: 2.2,
          pb: `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 24px)`,
        }}
      >
        <Stack spacing={1.6}>
          <Box>
            <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800 }}>
              Your trail
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
              History
            </Typography>
          </Box>

          <Box
            sx={(theme) => ({
              p: 2.2,
              borderRadius: 1,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? theme.palette.background.paper
                  : theme.colors.inssa,
              backgroundImage:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${alpha(theme.colors.mediumBlue, 0.42)} 0%, ${alpha(theme.colors.darkPurple, 0.72)} 100%)`
                  : `linear-gradient(135deg, ${theme.colors.inssa} 0%, ${theme.colors.mediumBlue} 100%)`,
              border:
                theme.palette.mode === "dark"
                  ? `1px solid ${alpha(theme.palette.text.primary, 0.08)}`
                  : "none",
              color: "common.white",
            })}
          >
            <Typography sx={{ color: "rgba(230,234,242,0.78)", fontSize: 12, fontWeight: 800, mb: 1.4 }}>
              Recently active
            </Typography>
            <Stack direction="row" spacing={2.2} alignItems="flex-start">
              <Stat label="capsules" value={summary?.capsules || 0} icon={<Inventory2RoundedIcon fontSize="small" />} />
              <Stat label="finds" value={summary?.finds || 0} icon={<SearchRoundedIcon fontSize="small" />} />
              <Stat label="received" value={summary?.received || 0} icon={<MarkEmailUnreadRoundedIcon fontSize="small" />} />
            </Stack>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              overflowX: "auto",
              pb: 0.4,
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            {filters.map((option) => (
              <Chip
                key={option.key}
                label={option.label}
                clickable
                onClick={() => setFilter(option.key)}
                color={filter === option.key ? "primary" : "default"}
                variant={option.secondary && filter !== option.key ? "outlined" : "filled"}
                sx={{ fontWeight: 800, flex: "0 0 auto" }}
              />
            ))}
          </Stack>

          <FormControl size="small" fullWidth>
            <InputLabel id="history-sort-label">Sort</InputLabel>
            <Select
              labelId="history-sort-label"
              label="Sort"
              value={sort}
              onChange={(event) => setSort(event.target.value)}
            >
              <MenuItem value="recent">Recently active</MenuItem>
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="oldest">Oldest</MenuItem>
            </Select>
          </FormControl>

          {error ? (
            <Alert severity="error">History is temporarily unavailable.</Alert>
          ) : null}

          {pageLoading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Stack>
          ) : visibleItems.length ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: "3px",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {visibleItems.map((item) => {
                const thumbnailUrl = historyTileThumbnailUrl(item);
                const hasThumbnail = Boolean(thumbnailUrl);
                const displayTitle = item?.type === "find" ? findDisplayTitle(item) : item.title;
                const showVisibilityAction = item?.filterType !== "loved";
                return (
                  <Box
                    key={item.id}
                    sx={(theme) => ({
                      position: "relative",
                      aspectRatio: "1 / 1",
                      overflow: "hidden",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.08)
                          : alpha(theme.colors?.paleBlue || theme.colors?.mediumBlue || "#4164D8", 0.08),
                    })}
                  >
                    <ButtonBase
                      component="div"
                      onClick={() => handleOpenItem(item)}
                      aria-label={`Open ${displayTitle}`}
                      sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        alignItems: "stretch",
                        justifyContent: "stretch",
                      }}
                    >
                    {hasThumbnail ? (
                      <Box
                        component="img"
                        src={thumbnailUrl}
                        alt={displayTitle || ""}
                        loading="lazy"
                        decoding="async"
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Stack alignItems="center" justifyContent="center" sx={{ width: "100%", height: "100%", pb: 2.5 }}>
                        <Box
                          sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color:
                              theme.palette.mode === "dark"
                                ? alpha(theme.palette.text.primary, 0.9)
                                : theme.colors.mediumBlue,
                            opacity: theme.palette.mode === "dark" ? 0.94 : 1,
                          })}
                        >
                          {fallbackIconFor(item)}
                        </Box>
                      </Stack>
                    )}
                    <Box
                      sx={(theme) => {
                        const isDark = theme.palette.mode === "dark";
                        const lightTextPanel = !hasThumbnail && !isDark;
                        return {
                          position: "absolute",
                          inset: "auto 0 0 0",
                          minHeight: 42,
                          px: 0.75,
                          py: 0.65,
                          background: lightTextPanel
                            ? "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.86) 28%, rgba(255,255,255,0.98) 100%)"
                            : "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.58) 38%, rgba(0,0,0,0.84) 100%)",
                        };
                      }}
                    >
                      <Typography
                        sx={(theme) => {
                          const useLightText = theme.palette.mode === "dark" || hasThumbnail;
                          return {
                            color: useLightText ? "common.white" : theme.palette.text.primary,
                            fontSize: 11,
                            fontWeight: 900,
                            lineHeight: 1.15,
                            textAlign: "left",
                            textShadow: useLightText ? "0 1px 2px rgba(0,0,0,0.75)" : "none",
                          };
                        }}
                      >
                        {isHiddenView ? "Hidden" : item.badge}
                      </Typography>
                      <Typography
                        sx={(theme) => {
                          const useLightText = theme.palette.mode === "dark" || hasThumbnail;
                          return {
                            color: useLightText ? "rgba(255,255,255,0.96)" : theme.palette.text.secondary,
                            fontSize: 10.5,
                            lineHeight: 1.2,
                            textAlign: "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textShadow: useLightText ? "0 1px 2px rgba(0,0,0,0.75)" : "none",
                          };
                        }}
                      >
                        {displayTitle}
                      </Typography>
                    </Box>
                    </ButtonBase>
                    {showVisibilityAction ? (
                      <IconButton
                        aria-label={`${isHiddenView ? "Unhide" : "Hide"} ${displayTitle} ${isHiddenView ? "in" : "from"} History`}
                        size="small"
                        onClick={(event) => (isHiddenView ? handleRestoreItem(event, item) : handleHideItem(event, item))}
                        sx={(theme) => ({
                          position: "absolute",
                          top: 4,
                          right: 4,
                          zIndex: 2,
                          width: 26,
                          height: 26,
                          color: theme.palette.common.white,
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? alpha(theme.palette.common.black, 0.38)
                              : alpha(theme.colors.inssa, 0.68),
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? alpha(theme.palette.common.black, 0.54)
                                : alpha(theme.colors.inssa, 0.82),
                          },
                        })}
                      >
                        {isHiddenView ? (
                          <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <VisibilityOffRoundedIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Stack alignItems="center" spacing={1.2} sx={{ py: 7, textAlign: "center" }}>
              <HistoryRoundedIcon sx={{ fontSize: 42, color: "text.secondary" }} />
              <Typography sx={{ fontWeight: 900 }}>{isHiddenView ? "No hidden items" : "No history yet"}</Typography>
              <Typography sx={{ color: "text.secondary", maxWidth: 300 }}>
                {isHiddenView
                  ? "Tiles you hide from History will appear here."
                  : filter === "loved"
                    ? "Love a time capsule to save it here."
                  : "Open capsules, receive memories, or run a successful Find to start your trail."}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Container>
      <Dialog
        open={Boolean(activeFind)}
        onClose={() => setActiveFind(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            m: 1.5,
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800 }}>
            Results from Find
          </Typography>
          <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }}>
            {findDisplayTitle(activeFind)}
          </Typography>
          {activeFind ? (
            <Typography sx={{ color: "text.secondary", fontSize: 13, fontWeight: 700, mt: 0.4 }}>
              {findAreaLabel(activeFind)}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {findResultsLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          ) : findResultsError ? (
            <Alert severity="error">{findResultsError}</Alert>
          ) : findResults.length ? (
            <Stack spacing={1}>
              {findResults.map((capsule) => {
                const imageUrl = firstCapsuleMediaUrl(capsule);
                return (
                  <ButtonBase
                    key={capsule.id}
                    onClick={() => {
                      setActiveFind(null);
                      navigate(`/capsule/${encodeURIComponent(capsule.id)}`);
                    }}
                    sx={(theme) => ({
                      width: "100%",
                      justifyContent: "flex-start",
                      gap: 1.2,
                      p: 1,
                      borderRadius: 1,
                      textAlign: "left",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.06)
                          : alpha(theme.colors.mediumBlue, 0.06),
                    })}
                  >
                    <Avatar
                      src={imageUrl || undefined}
                      variant="rounded"
                      sx={(theme) => ({
                        width: 54,
                        height: 54,
                        borderRadius: 0.5,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.colors.green, 0.18)
                            : alpha(theme.colors.mediumBlue, 0.12),
                      })}
                    >
                      {!imageUrl ? fallbackIconFor({ badge: "Find" }) : null}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                        {capsuleTitle(capsule)}
                      </Typography>
                      {capsuleSubtitle(capsule) ? (
                        <Typography sx={{ color: "text.secondary", fontSize: 12 }} noWrap>
                          {capsuleSubtitle(capsule)}
                        </Typography>
                      ) : null}
                    </Box>
                  </ButtonBase>
                );
              })}
            </Stack>
          ) : (
            <Typography sx={{ color: "text.secondary", py: 2 }}>
              No capsule results are available for this Find anymore.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      {hiddenSnackbar.open && (
        <Alert
          component="div"
          severity="success"
          sx={{
            position: "fixed",
            left: 16,
            right: 16,
            bottom: `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 12px)`,
            zIndex: 1500,
            display: "flex",
            alignItems: "center",
          }}
          action={
            <Button color="inherit" size="small" onClick={handleUndoHide}>
              Undo
            </Button>
          }
          onClose={() => setHiddenSnackbar({ open: false, item: null })}
        >
          Hidden from History
        </Alert>
      )}
    </Box>
  );
}
